import { type AudioHandle, type IAudioSink, type PcmAudio } from './KokoroTTSService';

/**
 * Plays Kokoro's PCM through a single `AudioContext`. Created lazily on first play so the context is
 * born inside the user's Play gesture — browsers refuse to start audio otherwise.
 *
 * The buffer keeps Kokoro's native sample rate (24kHz); the context resamples to the device rate on
 * playback. Speed is baked into the synthesis, so the source always plays at rate 1.
 */
export class WebAudioSink implements IAudioSink {
  private ctx: AudioContext | null = null;

  private context(): AudioContext {
    if (this.ctx === null) {
      const Ctor: typeof AudioContext =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();
    }
    // A context can be suspended by autoplay policy; resume is a no-op once running.
    void this.ctx.resume();
    return this.ctx;
  }

  play(pcm: PcmAudio, onEnded: () => void): AudioHandle {
    const ctx = this.context();
    const buffer = ctx.createBuffer(1, pcm.data.length, pcm.sampleRate);
    // `.set` (vs copyToChannel) sidesteps the DOM lib's Float32Array<ArrayBuffer> generic, which the
    // worker's transferred buffer does not statically satisfy.
    buffer.getChannelData(0).set(pcm.data);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.onended = onEnded;
    source.start();
    return source;
  }

  stop(handle: AudioHandle): void {
    const source = handle as AudioBufferSourceNode;
    // Drop the callback first: a manual stop() fires `onended` too, and the caller must not see it.
    source.onended = null;
    try {
      source.stop();
    } catch {
      // stop() throws if the node never started or already stopped; nothing to do.
    }
    source.disconnect();
  }
}
